// src/pages/DeliverySettings.tsx
import { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import { getAppSettings, updateAppSettings } from "../api/adminSettingsApi";

export default function DeliverySettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [settings, setSettings] = useState({
    local_delivery_enabled: false,
    default_delivery_fee_cents: 0,
    use_zones_for_delivery: true,
    delivery_eta_start: "30m",
    delivery_eta_end: "60m",
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const data = await getAppSettings();
        setSettings(data);
      } catch (err) {
        console.error(err);
        setError("Failed to load app settings");
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const updated = await updateAppSettings(settings);
      setSettings(updated.settings || updated);
      setSuccess("Settings saved successfully!");
    } catch (err) {
      console.error(err);
      setError("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <CircularProgress sx={{ mt: 5, mx: "auto", display: "block" }} />;

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", mt: 5 }}>
      <Card>
        <CardHeader title="Delivery Settings" />
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={settings.local_delivery_enabled}
                onChange={(e) =>
                  setSettings({ ...settings, local_delivery_enabled: e.target.checked })
                }
              />
            }
            label="Enable Local Delivery"
            sx={{ mb: 2 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={settings.use_zones_for_delivery}
                onChange={(e) =>
                  setSettings({ ...settings, use_zones_for_delivery: e.target.checked })
                }
              />
            }
            label="Use Delivery Zones"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Default Delivery Fee (in dollars)"
            type="number"
            value={(settings.default_delivery_fee_cents / 100).toFixed(2)}
            onChange={(e) =>
              setSettings({
                ...settings,
                default_delivery_fee_cents: Math.round(Number(e.target.value) * 100),
              })
            }
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="ETA Start"
              value={settings.delivery_eta_start}
              onChange={(e) =>
                setSettings({ ...settings, delivery_eta_start: e.target.value })
              }
            />
            <TextField
              fullWidth
              label="ETA End"
              value={settings.delivery_eta_end}
              onChange={(e) =>
                setSettings({ ...settings, delivery_eta_end: e.target.value })
              }
            />
          </Box>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ mt: 2, fontWeight: 600 }}
          >
            {saving ? "Saving..." : "Save Settings"}
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Note: If "Use Delivery Zones" is enabled, the system will calculate delivery fees
            based on the customer's address and matching polygon zone. Otherwise, the default
            delivery fee will be applied.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
